import { Document, Font, Image, Page, StyleSheet, Text, usePDF, View } from "@react-pdf/renderer";
import TGButton from "./ui/TGButton";
import { LookUpRecord } from "src/types/main";
import useAsyncEffect from "src/hooks/useAsyncEffect";
import { getUserProperties } from "src/modules/users/api";
import { getNameAndSurname } from "src/utils/users.utils";
import { getItemsRequest } from "src/modules/spItems/api";
import { MyTaskActionsListFieldsType, myTaskActionsListName } from "src/api/config/myTasksActions.config";
import { useSearchParams } from "react-router-dom";
import { modifiedItemsState } from "src/modules/spItems/selectors";
import { isEqual, uniqWith } from "lodash-es";
import { PrinterIcon } from "src/components/ui-icons/card";
import Loading from "./Loading";
import { TooltipItem } from "src/utils/Tooltip";
import { useState } from "react";
import { isStrapi } from "src/api/config/systemSwitch";
import parseMarkdownForPDF from "src/utils/parseMarkdownForPdf";

type QuestionType = {
  QuestionTitle: string;
  QuestionText: string;
  ID: number;
  Title: string;
  QuestionType: "radio" | "select" | "rate";
  TemplateOWD: LookUpRecord[];
  Options: Array<{
    ID: number;
    Title: string;
    isSelected?: boolean;
  }>;
  Answers: LookUpRecord[];
  selectedRate?: number;
};

type OWDCheckListPDFProps = {
  document: {
    Code: string;
    Title: string;
  };
  checkedBy: {
    name: string;
    personalNumber: string;
    avatar: string;
  };
  reviewedBy: {
    name: string;
    personalNumber: string;
    avatar: string;
  };
  sendedBy: {
    name: string;
    personalNumber: string;
    avatar: string;
  };
  reviewDate: string;
  todayDate: string;

  title: string; // название чек-листа
  completionPercentage: number;
  targetPercentage: number;
  questions: QuestionType[];
  comment: string;
  answers?: Record<string, any>;
  actions?: (MyTaskActionsListFieldsType[typeof myTaskActionsListName] & {
    ResponsibleEmail: Record<string, any> | null;
  })[];
};

interface OWDCheckListProps {
  document: {
    Code: string;
    Title: string;
  };
  checkedBy: string;
  reviewedBy: string;
  sendedBy: string;
  reviewDate: string;
  todayDate: string;

  title: string; // название чек-листа
  completionPercentage: number;
  targetPercentage: number;
  questions: QuestionType[];
  comment: string;
  answers?: Record<string, any>;
}

// Register Font
Font.register({
  family: "Roboto",
  fonts: [
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf",
      fontWeight: "light",
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf",
      fontWeight: "normal",
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf",
      fontWeight: "medium",
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf",
      fontWeight: "bold",
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-italic-webfont.ttf",
      fontWeight: "normal",
      fontStyle: "italic",
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bolditalic-webfont.ttf",
      fontWeight: "bold",
      fontStyle: "italic",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    fontFamily: "Roboto",
    padding: 30,
    fontSize: 12,
  },
  header: {
    marginBottom: 20,
  },
  section: {
    marginBottom: 15,
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderRadius: 4,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 10,
    color: "#012060",
  },
  row: {
    flexDirection: "row",
    marginBottom: 8,
    alignItems: "center",
  },
  label: {
    width: 150,
    color: "#637381",
    fontSize: 12,
    flexShrink: 0,
    alignSelf: "center",
  },
  value: {
    flex: 1,
    color: "#212B36",
    fontSize: 12,
    marginLeft: 24,
  },
  documentValue: {
    color: "#212B36",
    fontSize: 12,
    marginLeft: 24,
    flex: 1,
    flexShrink: 1,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 20,
    textAlign: "center",
    color: "#012060",
  },
  progressSection: {
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: 20,
    backgroundColor: "#f8f9fa",
    padding: 15,
    borderRadius: 4,
  },
  percentage: {
    fontSize: 16,
    color: "#dc3545",
    fontWeight: "bold",
  },
  separator: {
    marginHorizontal: 8,
    color: "#637381",
  },
  targetPercentage: {
    fontSize: 16,
    fontWeight: "bold",
  },
  questionSection: {
    marginBottom: 10,
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderRadius: 4,
    flexShrink: 1,
  },
  questionTitle: {
    marginBottom: 5,
    color: "#212B36",
    flexDirection: "column",
    width: "100%",
  },
  questionType: {
    fontSize: 10,
    color: "#637381",
    marginBottom: 8,
  },
  optionsContainer: {
    marginTop: 5,
  },
  optionRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 5,
  },
  radioButton: {
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#637381",
    marginRight: 8,
  },
  radioButtonSelected: {
    backgroundColor: "#0d6efd",
    borderColor: "#0d6efd",
  },
  optionLabel: {
    fontSize: 11,
    color: "#212B36",
  },
  rateContainer: {
    flexDirection: "row",
    gap: 8,
  },
  rateButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#637381",
    alignItems: "center",
    justifyContent: "center",
  },
  rateButtonSelected: {
    backgroundColor: "#0d6efd",
    borderColor: "#0d6efd",
  },
  rateText: {
    fontSize: 10,
    color: "#ffffff",
  },
  commentSection: {
    marginTop: 20,
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderRadius: 4,
  },
  commentText: {
    fontSize: 12,
    color: "#212B36",
    lineHeight: 1.5,
  },
  progressBarContainer: {
    backgroundColor: "#f3f4f6",
    borderColor: "#e5e7eb",
    borderWidth: 1,
    borderRadius: 4,
    padding: 10,
    marginVertical: 10,
  },
  text: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#ffffff",
  },
  checklistLabel: {
    position: "absolute",
    left: 30,
    top: "50%",
    transform: "rotate(-90deg)",
    fontSize: 24,
    color: "#637381",
    opacity: 0.5,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  checklistTitle: {
    fontSize: 24,
    color: "#012060",
    fontWeight: "bold",
  },
  selectedAnswer: {
    color: "#198754",
    fontWeight: "bold",
  },
  unselectedAnswer: {
    color: "#637381",
  },
  answerRow: {
    flexDirection: "row",
    marginLeft: 20,
    marginTop: 5,
    alignItems: "center",
  },
  selectedRadio: {
    backgroundColor: "#0d6efd",
    borderColor: "#0d6efd",
  },
  unselectedRadio: {
    borderColor: "#637381",
  },
  rowWithAvatar: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  avatarContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginLeft: 24,
  },
  avatar: {
    display: "flex",
    flexDirection: "column",
    width: 24,
    height: 24,
    borderRadius: 12,
    overflow: "hidden",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "blue",
  },
  avatarImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  avatarInitials: {
    color: "white",
    fontSize: 9,
    fontWeight: "bold",
    textAlign: "center",
  },
  avatarText: {
    display: "flex",
    flexDirection: "column",
    marginLeft: 8,
  },
  userName: {
    fontSize: 12,
    color: "#212B36",
    fontWeight: "medium",
    marginBottom: 2,
  },
  userNumber: {
    fontSize: 10,
    color: "#637381",
    letterSpacing: 0.5,
  },
  actionsSection: {
    marginTop: 20,
  },
  table: {
    marginTop: 16,
    width: "100%",
  },
  tableRow: {
    display: "flex",
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    padding: 8,
    backgroundColor: "#FFFFFF",
  },
  tableHeader: {
    display: "flex",
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    backgroundColor: "#F9FAFB",
    padding: 8,
  },
  columnDate: {
    width: "10%",
    fontSize: 10,
    color: "#637381",
    paddingRight: 8,
  },
  columnDescription: {
    width: "35%",
    fontSize: 10,
    color: "#212B36",
    paddingRight: 8,
  },
  columnMeeting: {
    width: "15%",
    fontSize: 10,
    color: "#212B36",
    paddingRight: 8,
  },
  columnDateDone: {
    width: "10%",
    fontSize: 10,
    color: "#637381",
    paddingRight: 8,
  },
  columnStatus: {
    width: "15%",
    flexDirection: "row",
    alignItems: "flex-start",
    paddingRight: 8,
  },
  columnResponsible: {
    width: "15%",
    flexDirection: "row",
    alignItems: "center",
  },
  statusContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 4,
    paddingRight: 8,
    paddingTop: 2,
  },
  responsibleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingRight: 8,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginTop: 4,
  },
  statusText: {
    fontSize: 10,
    color: "#212B36",
  },
  responsibleInfo: {
    flexDirection: "column",
    gap: 2,
  },
  responsibleName: {
    fontSize: 10,
    color: "#111928",
  },
  responsibleTitle: {
    fontSize: 9,
    color: "#637381",
  },
  headerText: {
    fontSize: 10,
    fontWeight: "medium",
    color: "#637381",
  },
  valueContainer: {
    flex: 1,
    marginLeft: 24,
  },
  textCell: {
    flexShrink: 1,
    flexGrow: 0,
  },
  columnText: {
    flexWrap: "wrap",
    wordBreak: "break-word",
  },
  responsibleNumber: {
    fontSize: 10,
    color: "#637381",
  },
});

const OWDCheckListPDF: React.FC<OWDCheckListPDFProps> = ({
  title,
  questions,
  document,
  checkedBy,
  reviewedBy,
  sendedBy,
  reviewDate,
  todayDate,
  completionPercentage,
  targetPercentage,
  comment,
  answers,
  actions,
}) => {
  const formatMomentDate = (date: any) => {
    if (!date) return "";
    return date.format("DD.MM.YYYY");
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>{title}</Text>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Информация</Text>
            <View style={styles.row}>
              <Text style={styles.label}>Документ:</Text>
              <Text style={styles.documentValue}>{`${document.Code} - ${document.Title}`}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Отправитель:</Text>
              <View style={styles.avatarContainer}>
                <View style={styles.avatar}>
                  {sendedBy.avatar ? (
                    <Image style={styles.avatarImage} src={sendedBy.avatar} />
                  ) : (
                    <Text style={styles.avatarInitials}>{getNameAndSurname(sendedBy.name, sendedBy.name, true)}</Text>
                  )}
                </View>
                <View style={styles.avatarText}>
                  <Text style={styles.userName}>{getNameAndSurname(sendedBy.name, sendedBy.name)}</Text>
                  <Text style={styles.userNumber}>{sendedBy.personalNumber}</Text>
                </View>
              </View>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Кто провел OWD:</Text>
              <View style={styles.avatarContainer}>
                <View style={styles.avatar}>
                  {checkedBy.avatar ? (
                    <Image style={styles.avatarImage} src={checkedBy.avatar} />
                  ) : (
                    <Text style={styles.avatarInitials}>{getNameAndSurname(checkedBy.name, checkedBy.name, true)}</Text>
                  )}
                </View>
                <View style={styles.avatarText}>
                  <Text style={styles.userName}>{getNameAndSurname(checkedBy.name, checkedBy.name)}</Text>
                  <Text style={styles.userNumber}>{checkedBy.personalNumber}</Text>
                </View>
              </View>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Кому провели OWD:</Text>
              <View style={styles.avatarContainer}>
                <View style={styles.avatar}>
                  {reviewedBy.avatar ? (
                    <Image style={styles.avatarImage} src={reviewedBy.avatar} />
                  ) : (
                    <Text style={styles.avatarInitials}>
                      {getNameAndSurname(reviewedBy.name, reviewedBy.name, true)}
                    </Text>
                  )}
                </View>
                <View style={styles.avatarText}>
                  <Text style={styles.userName}>{getNameAndSurname(reviewedBy.name, reviewedBy.name)}</Text>
                  <Text style={styles.userNumber}>{reviewedBy.personalNumber}</Text>
                </View>
              </View>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Срок проведения:</Text>
              <Text style={styles.value}>{reviewDate}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Дата проведения:</Text>
              <Text style={styles.value}>{todayDate}</Text>
            </View>
          </View>

          <View style={styles.headerRow}>
            <Text style={styles.checklistTitle}>Чек-лист</Text>
            <View style={styles.progressSection}>
              <Text
                style={[styles.percentage, { color: completionPercentage < targetPercentage ? "#dc3545" : "#198754" }]}
              >
                {Math.round(completionPercentage)}%
              </Text>
              <Text style={styles.separator}>/</Text>
              <Text style={styles.targetPercentage}>{targetPercentage}%</Text>
            </View>
          </View>
        </View>

        <View style={styles.optionsContainer}>
          {questions
            .sort((a, b) => a.QuestionText.localeCompare(b.QuestionText))
            .map((question, index) => (
              <View key={question.ID} style={styles.questionSection}>
                <View style={styles.questionTitle}>
                  {parseMarkdownForPDF(`${index + 1}. ${question.QuestionText}`)}
                </View>
                <Text style={styles.questionType}>
                  {question.QuestionType === "select" && "Можно выбрать несколько вариантов ответа"}
                  {question.QuestionType === "radio" && "Выберите 1 вариант ответа"}
                  {question.QuestionType === "rate" && "Оцените по шкале от 1 до 5"}
                </Text>

                <View style={styles.optionsContainer}>
                  {(question.QuestionType === "radio" || question.QuestionType === "select") &&
                    question.Options.map((option) => (
                      <View key={option.ID} style={styles.answerRow}>
                        <View
                          style={[
                            styles.radioButton,
                            answers && answers[question.ID] && answers[question.ID].ID === option.ID
                              ? styles.selectedRadio
                              : styles.unselectedRadio,
                          ]}
                        />
                        <Text style={option.isSelected ? styles.selectedAnswer : styles.unselectedAnswer}>
                          {option.Title}
                        </Text>
                      </View>
                    ))}

                  {question.QuestionType === "rate" && question.selectedRate && (
                    <View style={styles.answerRow}>
                      <Text style={styles.selectedAnswer}>Оценка: {question.selectedRate}</Text>
                    </View>
                  )}
                </View>
              </View>
            ))}
        </View>

        {comment && (
          <View style={styles.commentSection}>
            <Text style={styles.sectionTitle}>Комментарий</Text>
            <Text style={styles.commentText}>{comment}</Text>
          </View>
        )}

        {/* here actions */}
        {actions && actions.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Действия</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={[styles.columnDate, styles.headerText]}>Дата</Text>
                <Text style={[styles.columnDescription, styles.headerText]}>Описание</Text>
                <Text style={[styles.columnMeeting, styles.headerText]}>Совещание</Text>
                <Text style={[styles.columnDateDone, styles.headerText]}>Срок выполнения</Text>
                <Text style={[styles.columnStatus, styles.headerText]}>Статус</Text>
                <Text style={[styles.columnResponsible, styles.headerText]}>Ответственный</Text>
              </View>

              {actions.map((action) => (
                <View key={action.ID} style={styles.tableRow}>
                  <Text style={[styles.columnDate, styles.columnText]}>{formatMomentDate(action.Created)}</Text>
                  <Text style={[styles.columnDescription, styles.columnText]}>{action.Description}</Text>
                  <Text style={[styles.columnMeeting, styles.columnText]}>{action.Meeting?.Title || ""}</Text>
                  <Text style={[styles.columnDateDone, styles.columnText]}>{formatMomentDate(action.DateForDone)}</Text>
                  <View style={styles.columnStatus}>
                    <View style={styles.statusContainer}>
                      <View
                        style={[
                          styles.statusDot,
                          {
                            backgroundColor: isStrapi
                              ? action.Status?.Metadata.Color
                              : action.Status_x003a_Color?.Title,
                          },
                        ]}
                      />
                      <Text style={[styles.statusText, styles.columnText]}>{action.Status?.Title}</Text>
                    </View>
                  </View>
                  <View style={styles.columnResponsible}>
                    <View style={styles.responsibleContainer}>
                      <View style={styles.avatar}>
                        {action.ResponsibleEmail?.PictureURL ? (
                          <Image style={styles.avatarImage} src={action.ResponsibleEmail?.PictureURL} />
                        ) : (
                          <Text style={styles.avatarInitials}>
                            {getNameAndSurname(
                              action.ResponsibleEmail?.PreferredName,
                              action.ResponsibleEmail?.PreferredName,
                              true,
                            )}
                          </Text>
                        )}
                      </View>
                      <View style={styles.responsibleInfo}>
                        <View>
                          <Text style={styles.responsibleName}>
                            {getNameAndSurname(
                              action.ResponsibleEmail?.PreferredName,
                              action.ResponsibleEmail?.PreferredName,
                            )}
                          </Text>
                          <Text style={styles.responsibleNumber}>{action.ResponsibleEmail?.abiGlobalID}</Text>
                        </View>
                        <View>
                          <Text style={styles.responsibleTitle}>
                            {action.ResponsibleEmail?.JobTitleRus ||
                              action.ResponsibleEmail?.TitleRus ||
                              action.ResponsibleEmail?.JobTitle}
                          </Text>
                        </View>
                      </View>
                    </View>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}
      </Page>
    </Document>
  );
};

export function DownloadPDFButton({ data, loading }: { data: OWDCheckListProps; loading: boolean }) {
  const [instance, updateInstance] = usePDF();
  const [query, setQuery] = useSearchParams();
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = query.get("notificationCard");

  const handleDownload = async () => {
    if (!instance.url) return;

    setIsPdfLoading(true);
    setError(null);

    try {
      // Проверяем доступность URL
      const response = await fetch(instance.url);
      if (!response.ok) {
        throw new Error("PDF не готов к печати");
      }

      // Настройки окна
      const windowFeatures = "width=1000,height=800,menubar=no,toolbar=no,location=no,status=no";
      const printWindow = window.open(instance.url, "_blank", windowFeatures);

      if (printWindow) {
        printWindow.onload = () => {
          // Устанавливаем масштаб
          const style = printWindow.document.createElement("style");
          style.textContent = `
            @media print {
              body {
                transform: scale(0.7);
                transform-origin: top left;
              }
            }
          `;
          printWindow.document.head.appendChild(style);

          printWindow.print();
          setIsPdfLoading(false);
        };
      } else {
        throw new Error("Не удалось открыть окно печати");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Произошла ошибка при печати");
      setIsPdfLoading(false);
    }
  };

  useAsyncEffect(async () => {
    if (loading) return;
    setIsPdfLoading(true);
    setError(null);
    try {
      const sendedBy = await getUserProperties(data.sendedBy, [
        "PreferredName",
        "abiGlobalID",
        "UserName",
        "PictureURL",
      ]);

      let reviewedBy = await getUserProperties(data.reviewedBy, [
        "PreferredName",
        "abiGlobalID",
        "UserName",
        "PictureURL",
      ]).catch((err) => {
        return {
          PreferredName: data.reviewedBy,
          abiGlobalID: "(Нет номера)",
          UserName: "(Нет номера)",
          PictureURL: "",
        };
      });

      if (!reviewedBy?.UserName) {
        reviewedBy = {
          PreferredName: data.reviewedBy,
          abiGlobalID: "(Нет номера)",
          UserName: "(Нет номера)",
          PictureURL: "",
        };
      }

      const checkedBy = await getUserProperties(data.checkedBy, [
        "PreferredName",
        "abiGlobalID",
        "UserName",
        "PictureURL",
      ]);

      const actionResponse = await getItemsRequest({
        listName: myTaskActionsListName,
        filters: { ImportantHashTag: `#OWD_${id}*` },
      });

      const actions = modifiedItemsState(actionResponse.items, myTaskActionsListName);

      const tempActions: (MyTaskActionsListFieldsType[typeof myTaskActionsListName] & { ResponsibleEmail: any })[] =
        uniqWith(actions, isEqual);

      const promises = tempActions.map(async (action) => {
        if (action.ResponsibleEmail) {
          const userProps = await getUserProperties(action.ResponsibleEmail, [
            "PreferredName",
            "abiGlobalID",
            "UserName",
            "PictureURL",
            "JobTitleRus",
            "TitleRus",
            "JobTitle",
          ]);
          action.ResponsibleEmail = userProps;
        }

        return action;
      });

      const newActions = await Promise.all(promises);

      const newData = {
        ...data,
        sendedBy: {
          name: sendedBy?.PreferredName || "",
          personalNumber: sendedBy?.abiGlobalID || sendedBy?.UserName || "",
          avatar: sendedBy?.PictureURL || "",
        },
        reviewedBy: {
          name: reviewedBy?.PreferredName || "",
          personalNumber: reviewedBy?.abiGlobalID || reviewedBy?.UserName || "",
          avatar: reviewedBy?.PictureURL || "",
        },
        checkedBy: {
          name: checkedBy?.PreferredName || "",
          personalNumber: checkedBy?.abiGlobalID || checkedBy?.UserName || "",
          avatar: checkedBy?.PictureURL || "",
        },
        actions: newActions,
      };

      const pdf = <OWDCheckListPDF {...newData} />;
      if (pdf) {
        setTimeout(() => {
          updateInstance(pdf);
        }, 1000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ошибка при подготовке PDF");
    } finally {
      setIsPdfLoading(false);
    }
  }, [data, loading]);

  const isPdfReady = !!instance.url && !instance.loading;
  const isSpinning = instance.loading || isPdfLoading || !instance.url || loading;

  return (
    <TooltipItem
      tooltipsText={error || (isSpinning ? "Подготовка PDF..." : "Распечатать")}
      position="top-left"
      hoverClassName="hover-black-svg"
      textClassName="whitespace-wrap text-center text-black"
    >
      <TGButton
        disabled={!isPdfReady || isPdfLoading}
        iconOnly={true}
        icon={isSpinning ? <Loading /> : <PrinterIcon />}
        onClick={handleDownload}
      />
    </TooltipItem>
  );
}
